import { T, useGT } from 'gt-react';
import { SpinnerIcon, XIcon } from '@phosphor-icons/react';
import type {
  CapturedScreenshot,
  PendingAttachment,
} from './feedbackAttachmentRules';

function openPreview(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer')?.focus();
}

function Thumb({
  src,
  alt,
  title,
  onRemove,
  removeLabel,
  disabled,
  chip,
}: {
  src: string;
  alt: string;
  title?: string;
  onRemove: () => void;
  removeLabel: string;
  disabled?: boolean;
  chip?: React.ReactNode;
}) {
  return (
    <div className="relative shrink-0" title={title}>
      <button
        type="button"
        onClick={() => openPreview(src)}
        className="block cursor-zoom-in rounded border"
      >
        <img src={src} alt={alt} className="h-16 w-24 rounded object-cover" />
      </button>
      {chip && (
        <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] font-medium text-white">
          {chip}
        </span>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={onRemove}
        aria-label={removeLabel}
        className="absolute -right-1.5 -top-1.5 rounded-full border bg-background p-0.5 text-muted-foreground shadow hover:text-foreground"
      >
        <XIcon className="h-3 w-3" weight="bold" />
      </button>
    </div>
  );
}

/**
 * The strip of images going out with the report: captured page screenshots
 * (each tagged with the page it came from), then the user's own files. Every
 * item can be removed; clicking opens it full size.
 */
export function AttachmentThumbnails({
  screenshots,
  onRemoveScreenshot,
  files,
  onRemoveFile,
  isCapturing,
  disabled,
}: {
  screenshots: CapturedScreenshot[];
  onRemoveScreenshot: (id: string) => void;
  files: PendingAttachment[];
  onRemoveFile: (id: string) => void;
  isCapturing: boolean;
  disabled?: boolean;
}) {
  const gt = useGT();
  if (!isCapturing && screenshots.length === 0 && files.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-start gap-2">
      {screenshots.map((shot, index) => (
        <Thumb
          key={shot.id}
          src={shot.previewUrl}
          alt={gt('Screenshot {n}', {
            context: 'alt text for a captured page screenshot thumbnail',
            n: index + 1,
          })}
          title={shot.pageTitle || shot.pageUrl}
          onRemove={() => onRemoveScreenshot(shot.id)}
          removeLabel={gt('Remove screenshot {n}', {
            context: 'aria label on the remove button of a captured screenshot',
            n: index + 1,
          })}
          disabled={disabled}
          chip={
            <T context="chip on a captured page screenshot thumbnail">Page</T>
          }
        />
      ))}
      {isCapturing && (
        <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded border bg-muted">
          <SpinnerIcon className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
      {files.map((f) => (
        <Thumb
          key={f.id}
          src={f.previewUrl}
          alt={f.file.name}
          title={f.file.name}
          onRemove={() => onRemoveFile(f.id)}
          removeLabel={gt('Remove {name}', {
            context: 'aria label on the remove button of an attached image',
            name: f.file.name,
          })}
          disabled={disabled}
        />
      ))}
    </div>
  );
}
